import { Avatar, Button, Card, Dropdown, Menu, MenuProps, Space, Tag } from 'antd'
import Sider from 'antd/es/layout/Sider'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import noraLogo from '../../assets/nora-logo.png'
import { useAuth } from '../../contexts/AuthContext'
import { useSideMenu } from '../../contexts/SideMenuContext'
import { USER_UPDATED_EVENT } from '../../helpers/UserStorage'
import { UserModel } from '../../interfaces/UserModel'
import { internalRequestsApi } from '../../helpers/InternalRequestsApi'
import { clearLocalStoragePreservingConsent } from '../../services/storageConsent'
import NoraFloatingButton from '../NoraFloatingButton'
import noProfileImage from './assets/no-profile-image.jpg'
import * as S from './style'
export function SideMenu (): React.ReactElement {
  const [menuItemKey, setMenuItemKey] = useState<string[]>([])
  const [loadingData, setLoadingData] = useState<boolean>(true)
  const [isDropdownIconFlipped, setIsDropdownIconFlipped] = useState<boolean>(false)
  const [isNoraModalOpen, setIsNoraModalOpen] = useState<boolean>(false)
  const { isSideMenuOpen, toggleSideMenu } = useSideMenu()
  const [user, setUser] = useState<UserModel>({
    id_user: '',
    user_name: '',
    user_image: '',
    billing_data: {}
  })
  const [userPlan, setUserPlan] = useState<string>('')
  const [canRequestInternally, setCanRequestInternally] = useState(false)
  const { userCan, user: authUser } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const shouldShowClientsMenu = user.usage_profile === 'consultant'

  const doLogout = useCallback(() => {
    clearLocalStoragePreservingConsent()
    navigate('/login')
  }, [navigate])

  const handleNoraClick = useCallback(() => {
    setIsNoraModalOpen(true)
  }, [])

  const dropdownItems: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <a onClick={() => navigate('/configuracoes')} style={{ fontFamily: 'var(--font-primary)' }}>
          Ajustes
        </a>
      ),
      icon: <span className="material-symbols-outlined" style={{ fontSize: '1.2rem' }}>settings</span>
    },
    // {
    //   key: '2',
    //   label: (
    //     <a href="/meu-plano" style={{ fontFamily: 'var(--font-primary)' }}>
    //       Meu plano
    //     </a>
    //   ),
    //   icon: <span className="material-symbols-outlined" style={{ fontSize: '1.2rem' }}>credit_card</span>
    // },
    {
      key: '3',
      label: (
        <a onClick={doLogout} style={{ fontFamily: 'var(--font-primary)' }}>
          Sair
        </a>
      ),
      icon: <span className="material-symbols-outlined" style={{ fontSize: '1.2rem' }}>power_settings_new</span>,
      danger: true
    }
  ]

  const handleMobileItemTouch = useCallback((key: string, redirectTo: string) => {
    setMenuItemKey([key])
    toggleSideMenu()
    setTimeout(() => {
      navigate(redirectTo)
    }, 400)
  }, [navigate])

  const handleDesktopItemClick = useCallback((key: string, redirectTo: string) => {
    setMenuItemKey([key])
    navigate(redirectTo)
  }, [navigate])

  const handleAvatarDropdownClick = useCallback(() => {
    setIsDropdownIconFlipped((prevState) => !prevState)
  }, [])

  const handleSetUser = useCallback(() => {
    const userStringfied = window.localStorage.getItem('user')
    if (userStringfied) {
      setUser(JSON.parse(userStringfied))
      setLoadingData(false)
    }
  }, [])

  const handleSetPlan = useCallback(() => {
    if (!user) {
      setUserPlan('Carregando...')
      return
    }
    if (userCan('admin:all')) {
      setUserPlan('Administrador(a)')
      return
    }
    if (!user.billing_data['Forpy Pro']) {
      setUserPlan('Assinante')
      return
    }
    if (!user.billing_data['Forpy Pro'].plano) {
      setUserPlan('Assinante')
      return
    }
    setUserPlan(`Plano ${String(user.billing_data['Forpy Pro'].plano)}`)
  }, [user])

  const avatarDropdown: React.ReactElement = <>
    <Dropdown menu={{ items: dropdownItems }} trigger={['click']} placement="topRight" arrow onOpenChange={handleAvatarDropdownClick}>
      <a href="/" onClick={(e) => e.preventDefault()}>
        <Space>
          {user.user_name.split(' ').length >= 2 ? user.user_name.split(' ')[0] + ' ' + user.user_name.split(' ')[1] : user.user_name}
          <span className="material-symbols-outlined" style={isDropdownIconFlipped ? { transform: 'rotate(180deg)', transition: 'var(--transition)' } : { transform: 'rotate(0deg)', transition: 'var(--transition)' }}>keyboard_arrow_down</span>
        </Space>
      </a>
    </Dropdown>
  </>

  useEffect(() => {
    if (!location) return
    if (location.pathname.startsWith('/fichas-tecnicas')) {
      setMenuItemKey(['/fichas-tecnicas'])
      return
    }
    setMenuItemKey([location.pathname])
  }, [location])

  useEffect(() => {
    handleSetUser()
    window.addEventListener(USER_UPDATED_EVENT, handleSetUser)

    return () => {
      window.removeEventListener(USER_UPDATED_EVENT, handleSetUser)
    }
  }, [handleSetUser])

  useEffect(() => {
    handleSetPlan()
  }, [user])

  useEffect(() => {
    let active = true
    setCanRequestInternally(false)
    void internalRequestsApi.access()
      .then(access => { if (active) setCanRequestInternally(access.allowed) })
      .catch(() => {})
    return () => { active = false }
  }, [authUser?.email, authUser?.username])

  return <>
    <S.SiderDesktop>
      <Sider
        trigger={null}
        collapsible
        collapsed={!isSideMenuOpen}
        theme='light'
        style={{
          position: 'relative',
          paddingTop: 16,
          boxShadow: 'rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px'
        }}
      >
        {isSideMenuOpen && <S.Logo style={{ marginBottom: 16 }} />}
        {!isSideMenuOpen && <S.LogoSmall style={{ marginBottom: 34.65 }} />}
        <Button
          type="text"
          icon={isSideMenuOpen ? <span className="material-symbols-outlined" style={{ fontSize: '1.4rem', marginRight: 4 }}>keyboard_double_arrow_left</span> : <span className="material-symbols-outlined" style={{ fontSize: '1.4rem', marginRight: 4 }}>keyboard_double_arrow_right</span>}
          onClick={() => toggleSideMenu()}

          style={{
            width: 32,
            height: 32,
            position: 'absolute',
            top: '46px',
            right: '-33px',
            zIndex: '100',
            background: '#e6f4ff',
            borderRadius: '0px 16px 16px 0px',
            color: 'var(--blue-darker)',
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px'
          }}
        />
        <Menu
          style={{ fontFamily: 'var(--font-primary)', display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'space-between' }}
          theme="light"
          mode="inline"
          selectedKeys={menuItemKey}
          items={[
            {
              key: '/inicio',
              icon: <span className="material-symbols-outlined" style={{ fontSize: isSideMenuOpen ? '1.5rem' : '1.8rem' }}>home</span>,
              label: 'Início',
              onClick: (e) => handleDesktopItemClick(e.key, '/inicio'),
              className: 'menu-item-inicio'
            },
            ...(shouldShowClientsMenu
              ? [
                  {
                    key: '/clientes',
                    icon: <span className="material-symbols-outlined" style={{ fontSize: isSideMenuOpen ? '1.5rem' : '1.8rem' }}>business_center</span>,
                    label: 'Clientes',
                    onClick: () => handleDesktopItemClick('/clientes', '/clientes'),
                    className: 'sidemenu-feature menu-item-clientes'
                  }
                ]
              : []),
            {
              key: '/controle-qualidade',
              icon: <span className="material-symbols-outlined" style={{ fontSize: isSideMenuOpen ? '1.5rem' : '1.8rem' }}>checklist</span>,
              label: <>
                Qualidade <Tag style={{ borderRadius: 8, marginLeft: 12 }} color='var(--orange)'>Novo</Tag>
              </>,
              disabled: !userCan('quality_control:all'),
              className: 'sidemenu-feature menu-item-qualidade',
              children: [
                {
                  key: '/controle-qualidade',
                  label: 'Visão Geral',
                  onClick: (e) => handleDesktopItemClick(e.key, '/controle-qualidade'),
                  disabled: !userCan('quality_control:all'),
                  className: 'sidemenu-feature'
                },
                {
                  key: '/controle-qualidade/meus-checklists',
                  label: 'Meus checklists',
                  onClick: (e) => handleDesktopItemClick(e.key, '/controle-qualidade/meus-checklists'),
                  disabled: !userCan('quality_control:all'),
                  className: 'sidemenu-feature'
                },
                {
                  key: '/controle-qualidade/relatorios',
                  label: 'Relatórios',
                  onClick: (e) => handleDesktopItemClick(e.key, '/controle-qualidade/relatorios'),
                  disabled: !userCan('quality_control:all'),
                  className: 'sidemenu-feature'
                },
                {
                  key: '/controle-qualidade/acoes',
                  label: 'Ações',
                  onClick: (e) => handleDesktopItemClick(e.key, '/controle-qualidade/acoes'),
                  disabled: !userCan('quality_control:all'),
                  className: 'sidemenu-feature'
                }
              ]
            },
            {
              key: '/fichas-tecnicas',
              icon: <span className="material-symbols-outlined" style={{ fontSize: isSideMenuOpen ? '1.5rem' : '1.8rem' }}>receipt_long</span>,
              label: <>
                Fichas Técnicas  <Tag style={{ borderRadius: 8, marginLeft: 12 }} color='var(--orange)'>Novo</Tag>
                {!userCan('technical_sheets:all') && <Tag style={{ borderRadius: 8, marginLeft: 16 }} color='#20b765' onClick={() => navigate('/checkout')}>
                  Assinar
                </Tag>}
              </>,
              onClick: (e) => handleDesktopItemClick(e.key, '/fichas-tecnicas'),
              disabled: !userCan('technical_sheets:all'),
              className: 'sidemenu-feature menu-item-fichas-tecnicas'
            },
            {
              key: '/projetos',
              icon: <span className="material-symbols-outlined" style={{ fontSize: isSideMenuOpen ? '1.5rem' : '1.8rem' }}>folder</span>,
              label: <>
                Rotulagem
                {!userCan('read:project') && <Tag style={{ borderRadius: 8, marginLeft: 16 }} color='#20b765' onClick={() => navigate('/checkout')}>
                  Assinar
                </Tag>}
              </>,
              onClick: (e) => handleDesktopItemClick(e.key, '/projetos'),
              disabled: !userCan('read:project'),
              className: 'sidemenu-feature menu-item-projetos'
            },
            {
              key: '/meus-codigo-de-barras',
              icon: <span className="material-symbols-outlined" style={{ fontSize: isSideMenuOpen ? '1.5rem' : '1.8rem' }}>barcode</span>,
              label: 'Códigos de Barras',
              onClick: (e) => handleDesktopItemClick(e.key, '/meus-codigo-de-barras'),
              disabled: !userCan('read:barcode'),
              className: 'sidemenu-feature menu-item-codigos'
            },
            {
              key: '/rastreabilidade',
              icon: <span className="material-symbols-outlined" style={{ fontSize: isSideMenuOpen ? '1.5rem' : '1.8rem' }}>travel_explore</span>,
              label: <>
                Rastreabilidade <Tag style={{ borderRadius: 8, marginLeft: 12 }} color='var(--orange)'>Novo</Tag>
              </>,
              disabled: !userCan('traceability:all'),
              className: 'sidemenu-feature menu-item-rastreabilidade',
              children: [
                {
                  key: '/rastreabilidade',
                  label: 'Início',
                  onClick: (e) => handleDesktopItemClick(e.key, '/rastreabilidade'),
                  disabled: !userCan('traceability:all'),
                  className: 'sidemenu-feature'
                },
                {
                  key: '/rastreabilidade/fazendas',
                  label: 'Fazendas',
                  onClick: (e) => handleDesktopItemClick(e.key, '/rastreabilidade/fazendas'),
                  disabled: !userCan('traceability:all'),
                  className: 'sidemenu-feature'
                },
                {
                  key: '/rastreabilidade/itens-producao',
                  label: 'Produtos',
                  onClick: (e) => handleDesktopItemClick(e.key, '/rastreabilidade/itens-producao'),
                  disabled: !userCan('traceability:all'),
                  className: 'sidemenu-feature'
                },
                {
                  key: '/rastreabilidade/lotes',
                  label: 'Lotes de Produção',
                  onClick: (e) => handleDesktopItemClick(e.key, '/rastreabilidade/lotes'),
                  disabled: !userCan('traceability:all'),
                  className: 'sidemenu-feature'
                },
                {
                  key: '/rastreabilidade/paginas-publicas',
                  label: 'Páginas Públicas',
                  onClick: (e) => handleDesktopItemClick(e.key, '/rastreabilidade/paginas-publicas'),
                  disabled: !userCan('traceability:all'),
                  className: 'sidemenu-feature'
                }
              ]
            },
            {
              key: '/sac',
              icon: <span className="material-symbols-outlined" style={{ fontSize: isSideMenuOpen ? '1.5rem' : '1.8rem' }}>support_agent</span>,
              label: <>
                SAC <Tag style={{ borderRadius: 8, marginLeft: 12 }} color='var(--orange)'>Novo</Tag>
              </>,
              onClick: (e) => handleDesktopItemClick(e.key, '/sac'),
              disabled: !userCan('sac:all'),
              className: 'sidemenu-feature menu-item-sac'
            },
            {
              key: 'nora-chat',
              icon: <img src={noraLogo} alt="Nora" style={{ width: isSideMenuOpen ? '1.5rem' : '1.8rem', height: isSideMenuOpen ? '1.5rem' : '1.8rem', borderRadius: '50%' }} />,
              label: <>
                Fale com a Nora
              </>,
              onClick: handleNoraClick,
              className: 'menu-item-nora'
            },
            {
              key: '/legislacoes',
              icon: <span className="material-symbols-outlined" style={{ fontSize: isSideMenuOpen ? '1.5rem' : '1.8rem' }}>assignment</span>,
              label: 'Legislações',
              onClick: (e) => handleDesktopItemClick(e.key, '/legislacoes'),
              className: 'menu-item-legislacoes'
            }
          ]}
        />
        <Card loading={loadingData} style={{ marginTop: 'auto', fontFamily: 'var(--font-primary)', position: 'relative' }}>
          <Card.Meta
            avatar={user.user_image ? <Avatar src={user.user_image} size={50} /> : <Avatar src={noProfileImage} size={50} />}
            title={avatarDropdown}
            description={isSideMenuOpen ? <p style={{ fontSize: '0.75rem', position: 'absolute', top: '48px', left: '84px' }}>{userPlan}</p> : ''}
          />
        </Card>
        {userCan('admin:all') && <Button
          style={{ maxWidth: '90%', width: isSideMenuOpen ? '80%' : '60%', margin: '0 auto', marginTop: 8 }}
          type='primary'
          icon={<span className="material-symbols-outlined" style={{ fontSize: '1.25rem' }}>admin_panel_settings</span>}
          onClick={() => navigate('/admin')}
        >
          {isSideMenuOpen && 'Painel admin'}
        </Button>}
        {canRequestInternally && <Button type="link" onClick={() => navigate('/interno/solicitacoes')}>Solicitações internas</Button>}
        <p className='version-text'>V.2.00.0</p>
      </Sider>
    </S.SiderDesktop>
    <S.SiderMobile $isSideMenuOpen={isSideMenuOpen}>
      <Sider
        trigger={null}
        collapsible
        collapsedWidth='0px'
        collapsed={!isSideMenuOpen}
        theme='light'
        style={{
          position: 'absolute',
          paddingTop: 16,
          boxShadow: 'rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px',
          minHeight: '100vh',
          display: 'flex'
        }}
      >
        <S.Logo style={{ marginBottom: 16 }} />
        <Button
          type="text"
          icon={isSideMenuOpen ? <span className="material-symbols-outlined" style={{ fontSize: '1.4rem' }}>close</span> : <span className="material-symbols-outlined" style={{ fontSize: '1.4rem' }}>menu</span>}
          onClick={() => toggleSideMenu()}
          style={{
            width: 32,
            height: 32,
            position: 'absolute',
            top: '16px',
            right: `${isSideMenuOpen ? '16px' : '-49px'}`,
            zIndex: '100',
            background: '#e6f4ff',
            borderRadius: '4px',
            color: 'var(--blue-darker)',
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px'
          }}
        />
        <Menu
          style={{ fontFamily: 'var(--font-primary)', display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'space-between' }}
          theme="light"
          mode="inline"
          selectedKeys={menuItemKey}
          items={[
            {
              key: '/inicio',
              icon: <span className="material-symbols-outlined" style={{ fontSize: isSideMenuOpen ? '1.5rem' : '1.8rem' }}>home</span>,
              label: 'Início',
              onClick: (e) => handleMobileItemTouch(e.key, '/inicio'),
              className: 'menu-item-inicio'
            },
            ...(shouldShowClientsMenu
              ? [
                  {
                    key: '/clientes',
                    icon: <span className="material-symbols-outlined" style={{ fontSize: isSideMenuOpen ? '1.5rem' : '1.8rem' }}>business_center</span>,
                    label: 'Clientes',
                    onClick: () => handleMobileItemTouch('/clientes', '/clientes'),
                    className: 'sidemenu-feature menu-item-clientes'
                  }
                ]
              : []),
            {
              key: '/controle-qualidade',
              icon: <span className="material-symbols-outlined" style={{ fontSize: isSideMenuOpen ? '1.5rem' : '1.8rem' }}>checklist</span>,
              label: <>
                Qualidade <Tag style={{ borderRadius: 8, marginLeft: 12 }} color='var(--orange)'>Novo</Tag>
              </>,
              disabled: !userCan('quality_control:all'),
              className: 'sidemenu-feature menu-item-qualidade',
              children: [
                {
                  key: '/controle-qualidade',
                  label: 'Visão Geral',
                  onClick: (e) => handleMobileItemTouch(e.key, '/controle-qualidade'),
                  disabled: !userCan('quality_control:all'),
                  className: 'sidemenu-feature'
                },
                {
                  key: '/controle-qualidade/meus-checklists',
                  label: 'Meus checklists',
                  onClick: (e) => handleMobileItemTouch(e.key, '/controle-qualidade/meus-checklists'),
                  disabled: !userCan('quality_control:all'),
                  className: 'sidemenu-feature'
                },
                {
                  key: '/controle-qualidade/relatorios',
                  label: 'Relatórios',
                  onClick: (e) => handleMobileItemTouch(e.key, '/controle-qualidade/relatorios'),
                  disabled: !userCan('quality_control:all'),
                  className: 'sidemenu-feature'
                },
                {
                  key: '/controle-qualidade/acoes',
                  label: 'Ações',
                  onClick: (e) => handleMobileItemTouch(e.key, '/controle-qualidade/acoes'),
                  disabled: !userCan('quality_control:all'),
                  className: 'sidemenu-feature'
                }
              ]
            },
            {
              key: '/fichas-tecnicas',
              icon: <span className="material-symbols-outlined" style={{ fontSize: isSideMenuOpen ? '1.5rem' : '1.8rem' }}>receipt_long</span>,
              label: 'Fichas Técnicas',
              onClick: (e) => handleMobileItemTouch(e.key, '/fichas-tecnicas'),
              disabled: !userCan('technical_sheets:all'),
              className: 'sidemenu-feature menu-item-fichas-tecnicas'
            },
            {
              key: '/projetos',
              icon: <span className="material-symbols-outlined" style={{ fontSize: isSideMenuOpen ? '1.5rem' : '1.8rem' }}>folder</span>,
              label: 'Rotulagem',
              onClick: (e) => handleMobileItemTouch(e.key, '/projetos'),
              disabled: !userCan('read:project'),
              className: 'sidemenu-feature menu-item-projetos'
            },
            {
              key: '/codigo-de-barras',
              icon: <span className="material-symbols-outlined" style={{ fontSize: isSideMenuOpen ? '1.5rem' : '1.8rem' }}>barcode</span>,
              label: 'Códigos de Barras',
              onClick: () => handleMobileItemTouch('/meus-codigo-de-barras', '/meus-codigo-de-barras'),
              disabled: !userCan('read:barcode'),
              className: 'sidemenu-feature menu-item-codigos'
            },
            {
              key: '/legislacoes',
              icon: <span className="material-symbols-outlined" style={{ fontSize: isSideMenuOpen ? '1.5rem' : '1.8rem' }}>assignment</span>,
              label: 'Legislações',
              onClick: (e) => handleMobileItemTouch(e.key, '/legislacoes'),
              className: 'menu-item-legislacoes'
            },
            {
              key: '/rastreabilidade',
              icon: <span className="material-symbols-outlined" style={{ fontSize: isSideMenuOpen ? '1.5rem' : '1.8rem' }}>travel_explore</span>,
              label: <>
                Rastreabilidade <Tag style={{ borderRadius: 8, marginLeft: 12 }} color='var(--orange)'>Novo</Tag>
              </>,
              disabled: !userCan('traceability:all'),
              className: 'sidemenu-feature menu-item-rastreabilidade',
              children: [
                {
                  key: '/rastreabilidade',
                  label: 'Início',
                  onClick: (e) => handleMobileItemTouch(e.key, '/rastreabilidade'),
                  disabled: !userCan('traceability:all'),
                  className: 'sidemenu-feature'
                },
                {
                  key: '/rastreabilidade/fazendas',
                  label: 'Fazendas',
                  onClick: (e) => handleMobileItemTouch(e.key, '/rastreabilidade/fazendas'),
                  disabled: !userCan('traceability:all'),
                  className: 'sidemenu-feature'
                },
                {
                  key: '/rastreabilidade/itens-producao',
                  label: 'Produtos',
                  onClick: (e) => handleMobileItemTouch(e.key, '/rastreabilidade/itens-producao'),
                  disabled: !userCan('traceability:all'),
                  className: 'sidemenu-feature'
                },
                {
                  key: '/rastreabilidade/lotes',
                  label: 'Lotes de Produção',
                  onClick: (e) => handleMobileItemTouch(e.key, '/rastreabilidade/lotes'),
                  disabled: !userCan('traceability:all'),
                  className: 'sidemenu-feature'
                },
                {
                  key: '/rastreabilidade/paginas-publicas',
                  label: 'Páginas Públicas',
                  onClick: (e) => handleMobileItemTouch(e.key, '/rastreabilidade/paginas-publicas'),
                  disabled: !userCan('traceability:all'),
                  className: 'sidemenu-feature'
                }
              ]
            },
            {
              key: '/sac',
              icon: <span className="material-symbols-outlined" style={{ fontSize: isSideMenuOpen ? '1.5rem' : '1.8rem' }}>support_agent</span>,
              label: <>
                SAC <Tag style={{ borderRadius: 8, marginLeft: 12 }} color='var(--orange)'>Novo</Tag>
              </>,
              onClick: (e) => handleMobileItemTouch(e.key, '/sac'),
              disabled: !userCan('sac:all'),
              className: 'sidemenu-feature menu-item-sac'
            },
            {
              key: 'nora-chat-mobile',
              icon: <img src={noraLogo} alt="Nora" style={{ width: isSideMenuOpen ? '1.5rem' : '1.8rem', height: isSideMenuOpen ? '1.5rem' : '1.8rem', borderRadius: '50%' }} />,
              label: <>
                Fale com a Nora
              </>,
              onClick: handleNoraClick,
              className: 'menu-item-nora'
            }
          ]}
        />
        <Card loading={loadingData} style={{ marginTop: 'auto', fontFamily: 'var(--font-primary)', position: 'relative' }}>
          <Card.Meta
            avatar={user.user_image ? <Avatar src={user.user_image} size={50} /> : <Avatar src={noProfileImage} size={50} />}
            title={avatarDropdown}
            description={isSideMenuOpen ? <p style={{ fontSize: '0.75rem', position: 'absolute', top: '48px', left: '84px' }}>{userPlan}</p> : ''}
          />
        </Card>
        {userCan('admin:all') && <Button
          style={{ maxWidth: '90%', width: isSideMenuOpen ? '80%' : '60%', margin: '0 auto', marginTop: 8 }}
          type='primary'
          icon={<span className="material-symbols-outlined" style={{ fontSize: '1.25rem' }}>admin_panel_settings</span>}
          onClick={() => navigate('/admin')}
        >
          {isSideMenuOpen && 'Painel admin'}
        </Button>}
        {canRequestInternally && <Button type="link" onClick={() => handleMobileItemTouch('/interno/solicitacoes', '/interno/solicitacoes')}>Solicitações internas</Button>}
        <p className='version-text'>V.2.00.0</p>
      </Sider>
    </S.SiderMobile>
    <NoraFloatingButton
      isOpen={isNoraModalOpen}
      onToggle={setIsNoraModalOpen}
    />
  </>
}
